import { ChangeEvent, FC, useCallback, useEffect, useId, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ReactComponent as NotFoundIcon } from "assets/images/common/not-found.svg";
import { ReactComponent as PlusIcon } from "assets/images/common/plus.svg";

import Button from "components/Button";
import Pagination from "components/Pagination";
import SearchInput from "components/SearchInput";

import AssetCard from "modules/portfolios/AssetCard";

import useDebounce from "hooks/useDebounce";
import useRealtime from "hooks/useRealtime";

import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  changePage,
  createAssetRequest,
  getAssetListRequest,
  resetSuccessfullyAddedAsset,
} from "store/slices/assetSlice";
import { setModalCallbacks } from "store/slices/modalCallbacksRegistry";
import { hideModal, showModal } from "store/slices/modalSlice";
import { showToast } from "store/slices/toastSlice";

import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";
import { PAGES } from "utils/links";

import { CreateAssetRequest } from "entities/assets";
import { AssetFormValues, ModalType } from "entities/common";

import styles from "./style.module.scss";

type PropTypes = {
  id: number;
  companyId: string;
};

const PortfolioAssetsTab: FC<PropTypes> = ({ id, companyId }) => {
  const callbackKey = useId();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { items: assets, meta, successfullyAddedId } = useAppSelector((store) => store.assets);
  const initialParams = QueryHelper.getFromQueryString(location.search);
  const [search, setSearch] = useState(initialParams[QUERY_PARAMS.SEARCH] || "");
  const debouncedSearch = useDebounce(search, 500);

  const isSearching = debouncedSearch.trim().length > 0;

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    dispatch(changePage(1));
  };

  const handleChangePage = (page: number) => {
    dispatch(changePage(page));
  };

  const handleSaveAsset = (values?: unknown) => {
    const data = values as AssetFormValues;

    const body: CreateAssetRequest = {
      name: data.name,
      type: data.type || null,
      description: data.description || null,
      portfolio_id: Number(id),
      manager_id: Number(data.managerId),
    };

    dispatch(createAssetRequest({ body, images: data.images }));
  };

  const handleAddAsset = () => {
    setModalCallbacks(callbackKey, { onSave: handleSaveAsset });
    dispatch(
      showModal({
        modalType: ModalType.CREATE_ASSET,
        modalProps: { mode: "create", callbackKey, companyId },
      }),
    );
  };

  const handleNavigateToAsset = (assetId: number) => () => {
    navigate(`/assets/${assetId}`, { state: { returnTo: location.pathname + location.search } });
  };

  const handleGetAssets = useCallback(
    (page: number, search: string, silent = false) => {
      dispatch(
        getAssetListRequest(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.PORTFOLIO_ID]: id,
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PAGE_SIZE]: 10,
            [QUERY_PARAMS.SEARCH]: (search || "")?.trim()?.toLowerCase(),
          }),
          silent ? { silent: true } : undefined,
        ),
      );
    },
    [dispatch, id],
  );

  useEffect(() => {
    const urlPage = Number(initialParams[QUERY_PARAMS.PAGE]) || 1;
    dispatch(changePage(urlPage));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const query = QueryHelper.getQueryString({
      [QUERY_PARAMS.TAB]: "assets",
      [QUERY_PARAMS.SEARCH]: debouncedSearch.trim() || undefined,
      [QUERY_PARAMS.PAGE]: meta.page > 1 ? meta.page : undefined,
    });
    navigate(`${PAGES.PORTFOLIO_DETAIL.replace(":id", String(id))}${query}`, { replace: true });
  }, [debouncedSearch, meta.page, id, navigate]);

  useEffect(() => {
    handleGetAssets(meta.page, debouncedSearch);
  }, [debouncedSearch, meta.page, handleGetAssets]);

  const reload = useCallback(() => {
    handleGetAssets(meta.page, debouncedSearch, true);
  }, [handleGetAssets, meta.page, debouncedSearch]);

  useRealtime(["compliance", "incidents"], reload, { portfolioId: Number(id) });

  useEffect(() => {
    if (successfullyAddedId) {
      dispatch(hideModal());
      dispatch(resetSuccessfullyAddedAsset());
      dispatch(
        showToast({
          id: "successfully-added-asset",
          message: "The asset was successfully created.",
          type: "success",
          duration: 3000,
        }),
      );
      handleGetAssets(meta.page, debouncedSearch);
    }
  }, [successfullyAddedId, dispatch, handleGetAssets, meta.page, debouncedSearch]);

  return (
    <div className={styles.assets}>
      <div className={styles.toolbar}>
        <SearchInput
          name="search-assets"
          value={search}
          placeholder="Search assets..."
          className={styles.search}
          onChange={handleSearch}
        />

        <Button variant="primary" className={styles.addButton} onClick={handleAddAsset}>
          <span className={styles.addButtonText}>Add asset</span>
          <PlusIcon className={styles.addButtonIcon} />
        </Button>
      </div>

      {!!assets?.length ? (
        <>
          <div className={styles.grid}>
            {assets.map((asset) => (
              <AssetCard
                key={asset.id}
                onClick={handleNavigateToAsset(asset.id)}
                data={{
                  id: asset.id,
                  name: asset.name,
                  type: asset.type,
                  description: asset.description,
                  image: asset.images?.[0]?.url || undefined,
                  manager: asset.manager || undefined,
                  compliance: asset.compliance_score || null,
                  activeIncidents: asset.active_incidents,
                }}
              />
            ))}
          </div>

          {meta?.total_pages > 1 && (
            <Pagination total={meta.total_pages} page={meta.page} onPageChange={handleChangePage} variant="secondary" />
          )}
        </>
      ) : (
        <div className={styles.empty}>
          <NotFoundIcon />

          <p>{isSearching ? "No assets found" : "No assets yet"}</p>

          {!isSearching && (
            <Button variant="secondary" onClick={handleAddAsset}>
              Add first asset
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default PortfolioAssetsTab;
